import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mentalHealthApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { LogoHome } from '../components/LogoHome';
import { 
  Heart, ArrowLeft, Sun, Cloud, CloudRain, Loader2,
  Phone, ExternalLink, Sparkles, TrendingUp
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";
const NATURE_IMAGE = "https://images.unsplash.com/photo-1603276730862-cbf79a742aae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbmF0dXJlJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc2Nzc0NTg4OHww&ixlib=rb-4.1.0&q=85&w=800";

export default function WellbeingHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tips, setTips] = useState({ tips: [], resources: [] });
  const [checkins, setCheckins] = useState([]);
  const [showCheckinForm, setShowCheckinForm] = useState(false);
  
  // Check-in form state
  const [moodScore, setMoodScore] = useState([5]);
  const [stressLevel, setStressLevel] = useState([5]);
  const [workloadFeeling, setWorkloadFeeling] = useState('manageable');
  const [notes, setNotes] = useState('');
  const [requestSupervisorTalk, setRequestSupervisorTalk] = useState(false);

  useEffect(() => {
    loadWellbeingData();
  }, []);

  const loadWellbeingData = async () => {
    try {
      const [tipsRes, checkinsRes] = await Promise.all([
        mentalHealthApi.getTips(),
        mentalHealthApi.getCheckins()
      ]);
      setTips(tipsRes.data);
      setCheckins(checkinsRes.data);
    } catch (error) {
      console.error('Failed to load wellbeing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCheckin = async () => {
    setSubmitting(true);
    try {
      await mentalHealthApi.checkIn({
        mood_score: moodScore[0],
        stress_level: stressLevel[0],
        workload_feeling: workloadFeeling,
        notes: notes || null,
        request_supervisor_talk: requestSupervisorTalk
      });
      toast.success('Check-in recorded. Take care of yourself!');
      setShowCheckinForm(false);
      setRequestSupervisorTalk(false);
      loadWellbeingData();
    } catch (error) {
      toast.error('Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const getMoodEmoji = (score) => {
    if (score <= 3) return '😔';
    if (score <= 5) return '😐';
    if (score <= 7) return '🙂';
    return '😊';
  };

  const getStressColor = (level) => {
    if (level <= 3) return 'text-green-600';
    if (level <= 6) return 'text-amber-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden" data-testid="wellbeing-hub">
      {/* Decorative animated gradient blobs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-teal-400/40 blur-3xl animate-pulse-slow" />
        <div className="absolute top-1/3 -left-20 h-80 w-80 rounded-full bg-emerald-500/30 blur-3xl animate-blob-float" />
        <div className="absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-[30rem] w-[30rem] rounded-full bg-teal-300/35 blur-3xl animate-pulse-slow" style={{ animationDelay: '3s' }} />
        <div className="absolute top-10 right-1/4 h-64 w-64 rounded-full bg-cyan-300/30 blur-3xl animate-blob-float" style={{ animationDelay: '5s' }} />
        <div className="absolute top-1/2 right-1/3 h-48 w-48 rounded-full bg-amber-200/30 blur-3xl" />
      </div>

      {/* Floating sparkles & geometric accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden hidden lg:block">
        <svg className="absolute top-32 right-32 h-6 w-6 opacity-60 animate-twinkle" viewBox="0 0 24 24" fill="rgb(20 184 166)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute bottom-1/3 right-12 h-4 w-4 opacity-70 animate-twinkle" style={{ animationDelay: '1.5s' }} viewBox="0 0 24 24" fill="rgb(16 185 129)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute top-1/2 left-1/3 h-5 w-5 opacity-55 animate-twinkle" style={{ animationDelay: '0.8s' }} viewBox="0 0 24 24" fill="rgb(6 182 212)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute bottom-32 right-1/3 h-4 w-4 opacity-60 animate-twinkle" style={{ animationDelay: '2.2s' }} viewBox="0 0 24 24" fill="rgb(245 158 11)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <div className="absolute top-[18%] right-[12%] h-3 w-3 rounded-full bg-teal-500/80 animate-float-1" />
        <div className="absolute top-[55%] right-[8%] h-3 w-3 rounded-full bg-emerald-500/75 animate-float-2" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-[18%] right-[40%] h-2.5 w-2.5 rotate-45 bg-cyan-500/70 animate-float-3" style={{ animationDelay: '1s' }} />
        <div className="absolute top-[35%] left-[8%] h-2.5 w-2.5 rounded-full bg-amber-500/70 animate-float-1" style={{ animationDelay: '3s' }} />
        <svg className="absolute top-1/4 right-1/3 h-24 w-24 opacity-25 animate-spin-slow" viewBox="0 0 100 100" fill="none">
          <polygon points="50,10 85,30 85,70 50,90 15,70 15,30" stroke="rgb(20 184 166)" strokeWidth="1" fill="none" />
          <polygon points="50,25 70,37 70,63 50,75 30,63 30,37" stroke="rgb(45 212 191)" strokeWidth="0.7" fill="rgb(20 184 166 / 0.1)" />
        </svg>
        <svg className="absolute bottom-12 left-1/3 h-28 w-28 opacity-20 animate-spin-slow" style={{ animationDirection: 'reverse' }} viewBox="0 0 200 200" fill="none">
          <circle cx="100" cy="100" r="80" stroke="rgb(20 184 166)" strokeWidth="0.7" />
          <circle cx="100" cy="100" r="60" stroke="rgb(20 184 166)" strokeWidth="0.7" strokeDasharray="2 4" />
          <circle cx="100" cy="100" r="40" stroke="rgb(45 212 191)" strokeWidth="0.7" />
        </svg>
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-teal-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>
            <LogoHome className="h-12 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Hero Section */}
        <div className="relative rounded-2xl overflow-hidden mb-8 shadow-2xl shadow-teal-500/40 ring-1 ring-teal-300/40">
          {/* Blended Visitex teal/emerald/cyan gradient base */}
          <div className="absolute inset-0 bg-gradient-to-br from-teal-400 via-emerald-500 to-cyan-600" />
          {/* Glowing radial highlight (top-left) — bright cyan */}
          <div className="absolute -top-24 -left-16 h-80 w-80 rounded-full bg-cyan-300/60 blur-3xl animate-pulse-slow" />
          {/* Mid emerald glow */}
          <div className="absolute top-1/4 left-1/3 h-64 w-64 rounded-full bg-emerald-300/50 blur-3xl animate-blob-float" />
          {/* Soft mint sweep (right) */}
          <div className="absolute -bottom-20 right-0 h-96 w-96 rounded-full bg-teal-200/50 blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
          {/* Deeper teal bloom (centre-bottom) */}
          <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-teal-400/45 blur-3xl animate-blob-float" style={{ animationDelay: '3s' }} />
          {/* Subtle indigo accent for depth */}
          <div className="absolute top-1/2 right-1/4 h-48 w-48 rounded-full bg-indigo-400/25 blur-3xl" />
          {/* Sparkles */}
          <svg className="absolute top-6 right-10 h-5 w-5 opacity-80 animate-twinkle" viewBox="0 0 24 24" fill="rgb(207 250 254)">
            <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
          </svg>
          <svg className="absolute bottom-8 right-1/3 h-4 w-4 opacity-90 animate-twinkle" style={{ animationDelay: '1.5s' }} viewBox="0 0 24 24" fill="rgb(255 255 255)">
            <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
          </svg>
          <svg className="absolute top-10 right-1/3 h-3 w-3 opacity-70 animate-twinkle" style={{ animationDelay: '0.8s' }} viewBox="0 0 24 24" fill="rgb(167 243 208)">
            <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
          </svg>
          {/* Subtle dotted texture for depth */}
          <div
            className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
            style={{
              backgroundImage: 'radial-gradient(rgb(255 255 255) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }}
          />
          <div className="relative p-8 md:p-12">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="h-8 w-8 text-white drop-shadow-lg" fill="currentColor" />
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
                Wellbeing Hub
              </h1>
            </div>
            <p className="text-teal-50 text-lg max-w-xl mb-6 drop-shadow">
              Your mental health matters. Take a moment to check in with yourself and access support resources.
            </p>
            <Button
              onClick={() => setShowCheckinForm(true)}
              className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg hover:shadow-xl transition-shadow"
              data-testid="start-checkin-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Start Check-In
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Check-in Form */}
            {showCheckinForm && (
              <Card className="border-teal-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Heart className="h-5 w-5 text-teal-600" />
                    Mental Health Check-In
                  </CardTitle>
                  <CardDescription>
                    How are you feeling today? This is confidential.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Mood Score */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-base">How's your mood?</Label>
                      <span className="text-3xl">{getMoodEmoji(moodScore[0])}</span>
                    </div>
                    <Slider
                      value={moodScore}
                      onValueChange={setMoodScore}
                      min={1}
                      max={10}
                      step={1}
                      className="py-4"
                      data-testid="mood-slider"
                    />
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>Low</span>
                      <span className="font-medium text-slate-900">{moodScore[0]}/10</span>
                      <span>Great</span>
                    </div>
                  </div>

                  {/* Stress Level */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-base">Stress level</Label>
                      <span className={`font-medium ${getStressColor(stressLevel[0])}`}>
                        {stressLevel[0] <= 3 ? 'Low' : stressLevel[0] <= 6 ? 'Moderate' : 'High'}
                      </span>
                    </div>
                    <Slider
                      value={stressLevel}
                      onValueChange={setStressLevel}
                      min={1}
                      max={10}
                      step={1}
                      className="py-4"
                      data-testid="stress-slider"
                    />
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>Relaxed</span>
                      <span className="font-medium text-slate-900">{stressLevel[0]}/10</span>
                      <span>Very Stressed</span>
                    </div>
                  </div>

                  {/* Workload Feeling */}
                  <div className="space-y-4">
                    <Label className="text-base">How does your workload feel?</Label>
                    <RadioGroup value={workloadFeeling} onValueChange={setWorkloadFeeling} className="grid grid-cols-3 gap-3">
                      <div>
                        <RadioGroupItem value="light" id="light" className="peer sr-only" />
                        <Label
                          htmlFor="light"
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-teal-500 cursor-pointer transition-colors"
                        >
                          <Sun className="h-6 w-6 mb-2 text-amber-500" />
                          <span className="text-sm font-medium">Light</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="manageable" id="manageable" className="peer sr-only" />
                        <Label
                          htmlFor="manageable"
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-teal-500 cursor-pointer transition-colors"
                        >
                          <Cloud className="h-6 w-6 mb-2 text-slate-500" />
                          <span className="text-sm font-medium">Manageable</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="overwhelming" id="overwhelming" className="peer sr-only" />
                        <Label
                          htmlFor="overwhelming"
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-teal-500 cursor-pointer transition-colors"
                        >
                          <CloudRain className="h-6 w-6 mb-2 text-blue-500" />
                          <span className="text-sm font-medium">Overwhelming</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="text-base">Any thoughts you'd like to share? (Optional)</Label>
                    <Textarea
                      placeholder="Type anything on your mind..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      data-testid="checkin-notes"
                    />
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50" data-testid="request-supervisor-section">
                    <Checkbox
                      id="request-supervisor-talk"
                      checked={requestSupervisorTalk}
                      onCheckedChange={setRequestSupervisorTalk}
                      data-testid="request-supervisor-checkbox"
                    />
                    <div>
                      <label htmlFor="request-supervisor-talk" className="text-sm font-medium text-slate-700 cursor-pointer">
                        I'd like to request a talk with my supervisor
                      </label>
                      <p className="text-xs text-slate-500 mt-0.5">Your supervisor will be notified that you'd like to have a conversation. This is confidential.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowCheckinForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmitCheckin}
                      disabled={submitting}
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                      data-testid="submit-checkin-btn"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Submit Check-In
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Burnout Prevention Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Burnout Prevention Tips</CardTitle>
                <CardDescription>Practical strategies for self-care</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {tips.tips.map((tip, index) => (
                    <div key={index} className="p-4 rounded-lg bg-slate-50 border border-slate-100 hover:border-teal-200 transition-colors">
                      <h3 className="font-medium text-slate-900 mb-1">{tip.title}</h3>
                      <p className="text-sm text-slate-600">{tip.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Check-ins */}
            {checkins.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-teal-600" />
                    Your Recent Check-Ins
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {checkins.slice(0, 5).map((checkin) => (
                      <div key={checkin.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                        <div className="flex items-center gap-4">
                          <span className="text-2xl">{getMoodEmoji(checkin.mood_score)}</span>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              Mood: {checkin.mood_score}/10 • Stress: {checkin.stress_level}/10
                            </p>
                            <p className="text-xs text-slate-500 capitalize">{checkin.workload_feeling} workload</p>
                            {checkin.request_supervisor_talk && (
                              <span className="inline-flex items-center mt-1 text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="supervisor-talk-badge">
                                Supervisor talk requested
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(checkin.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Crisis Support */}
            <Card className="bg-red-50 border-red-200">
              <CardHeader>
                <CardTitle className="font-heading text-red-800">Need Immediate Support?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {tips.resources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg bg-white border border-red-100 hover:border-red-300 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-red-900">{resource.name}</p>
                      <p className="text-sm text-red-700 flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {resource.phone}
                      </p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-red-400" />
                  </a>
                ))}
              </CardContent>
            </Card>

            {/* Peer Support */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Peer Support</CardTitle>
                <CardDescription>Connect with colleagues</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  <div className="h-16 w-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-8 w-8 text-teal-600" />
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Remember, you're not alone. Reach out to a trusted colleague or supervisor if you need support.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
